import { useState, useMemo } from "react";
import { ArrowLeft, ArrowRight, Info } from "lucide-react";
import { QuestionnairePage, ResultPanel } from "./SBCQualifier";

const QUESTIONS = [
    {
        id: "entity",
        question: "Which best describes you?",
        helper: "Companies and trusts are automatically provisional taxpayers. Individuals only fall in based on the type of income they earn.",
        options: [
            { label: "A registered company / CC", value: "company" },
            { label: "A trust", value: "trust" },
            { label: "An individual (sole trader, freelancer or salary earner)", value: "individual" },
        ],
        autoResolve: (v) => {
            if (v === "company") return { kind: "fail", reason: "All companies and close corporations are automatically provisional taxpayers under paragraph 1 of the Fourth Schedule. The company must register and submit IRP6/01 (six months into the year) and IRP6/02 (at year-end), with an optional IRP6/03 top-up." };
            if (v === "trust") return { kind: "fail", reason: "Trusts (other than special trusts type A) are automatically provisional taxpayers. The trustees must register the trust and submit IRP6 returns twice a year." };
            return null;
        },
    },
    {
        id: "salaryOnly",
        question: "Is your ONLY income remuneration from an employer who deducts PAYE for you each month?",
        helper: "If your only income is salary subject to PAYE you generally don't need to register as a provisional taxpayer.",
        options: [
            { label: "Yes — salary only, with PAYE deducted", value: "yes" },
            { label: "No — I have other income too (rental, freelance, business, dividends from private companies, large interest)", value: "no" },
        ],
        evaluate: (v) =>
            v === "yes"
                ? { kind: "pass", reason: "If your only income is remuneration on which PAYE is deducted, you fall outside the provisional-taxpayer net — no IRP6 returns required." }
                : { kind: "ok" },
    },
    {
        id: "otherIncome",
        question: "Will the income that is NOT subject to PAYE (rental, freelance, business profits, interest above the exemption) exceed R30,000 in this year of assessment?",
        helper: "The R30,000 'practical' threshold (in the Fourth Schedule's exclusion paragraph) is the line for individuals.",
        options: [
            { label: "Yes — more than R30,000", value: "yes" },
            { label: "No — less than R30,000", value: "no" },
            { label: "Not sure", value: "maybe" },
        ],
        evaluate: (v) => {
            if (v === "yes") return { kind: "fail", reason: "Once non-PAYE taxable income for an individual exceeds R30,000 in the year, you become a provisional taxpayer and must submit IRP6/01 and IRP6/02." };
            if (v === "maybe") return { kind: "maybe", reason: "If you are close to R30,000 of non-PAYE income, plan as if you will be a provisional taxpayer — IRP6/01 falls due 6 months into the year and the under-estimation penalty is steep." };
            return { kind: "ok" };
        },
    },
    {
        id: "age",
        question: "Are you 65 years or older AND your only non-remuneration income is interest, dividends, or rental from immovable property — totalling less than R120,000 a year?",
        helper: "Pensioners with limited investment income are specifically excluded from the provisional-taxpayer net.",
        options: [
            { label: "Yes — I meet that pensioner exclusion", value: "yes" },
            { label: "No — younger than 65, or income exceeds the limit", value: "no" },
        ],
        evaluate: (v) =>
            v === "yes"
                ? { kind: "pass", reason: "Paragraph 18 of the Fourth Schedule excludes natural persons aged 65+ whose taxable income consists only of remuneration, interest, dividends or fixed-property rental and totals less than R120,000 — you are NOT a provisional taxpayer." }
                : { kind: "ok" },
    },
    {
        id: "highIncome",
        question: "Will your total taxable income for the year exceed R1 million?",
        helper: "Above R1m, the provisional-tax under-estimation penalty rules apply at 80% rather than 90%.",
        options: [
            { label: "Yes — over R1m", value: "yes" },
            { label: "No — under R1m", value: "no" },
        ],
        evaluate: (v) =>
            v === "yes"
                ? { kind: "fail", reason: "If you are an individual earning more than R1m in taxable income (and any portion is not subject to PAYE), you are firmly a provisional taxpayer. Your second IRP6 estimate must be at least 80% of actual taxable income — under-estimation triggers a 20% penalty." }
                : { kind: "ok" },
    },
];

export default function ProvisionalTaxQualifier() {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const [autoResult, setAutoResult] = useState(null);

    const current = QUESTIONS[step];
    const total = QUESTIONS.length;
    const finished = step >= total;

    const result = useMemo(() => {
        if (autoResult) return autoResult;
        if (!finished) return null;
        const evals = QUESTIONS.map((q) => (answers[q.id] !== undefined ? q.evaluate?.(answers[q.id]) : null));
        const fails = evals.filter((e) => e && e.kind === "fail");
        const passes = evals.filter((e) => e && e.kind === "pass");
        const maybes = evals.filter((e) => e && e.kind === "maybe");
        if (passes.length) return { kind: "pass", details: passes };
        if (fails.length) return { kind: "fail", details: fails };
        if (maybes.length) return { kind: "maybe", details: maybes };
        return { kind: "maybe", details: [] };
    }, [answers, finished, autoResult]);

    const choose = (val) => {
        setAnswers((a) => ({ ...a, [current.id]: val }));
        if (current.autoResolve) {
            const auto = current.autoResolve(val);
            if (auto) {
                setAutoResult({ kind: auto.kind, details: [auto] });
                setStep(total);
                return;
            }
        }
        setStep((s) => s + 1);
    };

    const reset = () => {
        setAnswers({});
        setAutoResult(null);
        setStep(0);
    };

    return (
        <QuestionnairePage
            title="Provisional Taxpayer Status Check"
            subtitle="Paragraph 1, Fourth Schedule — Income Tax Act"
            current={step}
            total={total}
            onReset={reset}
            testIdRoot="prov"
        >
            {!finished && current && (
                <div className="space-y-7" data-testid={`prov-question-${step}`}>
                    <div>
                        <p className="overline text-tca-orange mb-3">Question {step + 1} of {total}</p>
                        <h2 className="font-display font-extrabold text-tca-ink text-2xl sm:text-3xl leading-tight">{current.question}</h2>
                        {current.helper && (
                            <p className="mt-3 text-sm text-tca-mute leading-relaxed flex items-start gap-2">
                                <Info className="w-4 h-4 mt-0.5 text-tca-orange shrink-0" /> {current.helper}
                            </p>
                        )}
                    </div>
                    <div className="space-y-2.5">
                        {current.options.map((o) => (
                            <button
                                key={o.label}
                                onClick={() => choose(o.value)}
                                data-testid={`prov-option-${step}-${o.value}`}
                                className="w-full text-left bg-white border border-tca-line hover:border-tca-orange hover:bg-tca-orangeSoft/50 transition-all px-5 py-4 group flex items-center justify-between gap-3"
                            >
                                <span className="font-medium text-tca-ink">{o.label}</span>
                                <ArrowRight className="w-4 h-4 text-tca-mute group-hover:text-tca-orange transition-colors" />
                            </button>
                        ))}
                    </div>
                    {step > 0 && (
                        <button onClick={() => setStep(step - 1)} data-testid={`prov-back-${step}`} className="btn-ghost">
                            <ArrowLeft className="w-4 h-4" /> Back
                        </button>
                    )}
                </div>
            )}

            {finished && result && (
                <ResultPanel
                    result={result}
                    passTitle="You are NOT a provisional taxpayer this year"
                    passBody="Based on your answers, you don't fall inside the provisional-taxpayer definition — your annual ITR12 alone is enough."
                    failTitle="You ARE a provisional taxpayer"
                    failBody="Here is why — and what it means for you:"
                    maybeTitle="Borderline — likely a provisional taxpayer"
                    maybeBody="Your facts are close to a threshold. Treat yourself as a provisional taxpayer to be safe and we'll confirm in a meeting:"
                    onReset={reset}
                />
            )}
        </QuestionnairePage>
    );
}
